import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useBeforeUnload, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  FormGroup,
  Input,
  Label,
  Progress,
  Row,
  Table,
} from 'reactstrap';
import { GlobalContext } from '../../api/GlobalContext';
import { logGAEvent } from '../../api/googleAnalytics';
import { getScoreSurfaceLabel } from '../../components/Misc/complianceUtils';
import ConfirmationModal from '../../components/Misc/ConfirmationModal';
import EditableLabel from '../../components/Misc/EditableLabel';
import Modal from '../../components/Modals/Modal';

// Constants
const WEIGHT_REQUIRED = 8;
const WEIGHT_OPTIONAL = 5;
const STRUCTURE_TYPE_OLD = 'old';
const STRUCTURE_TYPE_NEW = 'new';

// Note types are defined by the backend; avoid FE-side canonicalization to
// keep a single source of truth. Only trim and dedupe.
const normalizeNoteType = (noteType) => {
  if (!noteType || typeof noteType !== 'string') return null;
  const trimmed = noteType.trim();
  return trimmed.length ? trimmed : null;
};

const normalizeNoteTypeArray = (noteTypes = []) => {
  return Array.from(
    new Set(
      (noteTypes || []).map(normalizeNoteType).filter(Boolean),
    ),
  );
};

const normalizeNoteTypeObject = (noteTypeObj = {}) => {
  const normalized = {};
  Object.entries(noteTypeObj || {}).forEach(([key, value]) => {
    const normKey = normalizeNoteType(key);
    if (!normKey) return;
    // If any variation is true, keep true
    if (value === true) {
      normalized[normKey] = true;
    } else if (!(normKey in normalized)) {
      normalized[normKey] = value;
    }
  });
  return normalized;
};

// Normalize note type labels inside criteria (both old/new structures)
const normalizeCriteriaNoteTypes = (criteria = {}) => {
  const normalizedCriteria = {};

  Object.entries(criteria || {}).forEach(([sectionName, categoryData]) => {
    if (Array.isArray(categoryData)) {
      // Old structure: array of requirements
      normalizedCriteria[sectionName] = categoryData.map((req) => {
        if (!req || typeof req !== 'object') return req;
        const updated = { ...req };
        if (Array.isArray(updated.note_types)) {
          updated.note_types = normalizeNoteTypeArray(updated.note_types);
        } else if (updated.note_types && typeof updated.note_types === 'object') {
          updated.note_types = normalizeNoteTypeObject(updated.note_types);
        }
        return updated;
      });
    } else if (categoryData && typeof categoryData === 'object') {
      const updatedCategory = { ...categoryData };

      if (Array.isArray(updatedCategory.category_note_types)) {
        updatedCategory.category_note_types = normalizeNoteTypeArray(
          updatedCategory.category_note_types,
        );
      } else if (
        updatedCategory.category_note_types &&
        typeof updatedCategory.category_note_types === 'object'
      ) {
        updatedCategory.category_note_types = normalizeNoteTypeObject(
          updatedCategory.category_note_types,
        );
      }

      if (Array.isArray(updatedCategory.requirements)) {
        updatedCategory.requirements = updatedCategory.requirements.map((req) => {
          if (!req || typeof req !== 'object') return req;
          const updatedReq = { ...req };
          if (Array.isArray(updatedReq.note_types)) {
            updatedReq.note_types = normalizeNoteTypeArray(updatedReq.note_types);
          } else if (
            updatedReq.note_types &&
            typeof updatedReq.note_types === 'object'
          ) {
            updatedReq.note_types = normalizeNoteTypeObject(updatedReq.note_types);
          }
          return updatedReq;
        });
      }

      normalizedCriteria[sectionName] = updatedCategory;
    } else {
      normalizedCriteria[sectionName] = categoryData;
    }
  });

  return normalizedCriteria;
};

// Shared styles for note types container
const noteTypesContainerStyle = {
  marginTop: '12px',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  flexWrap: 'nowrap',
  overflowX: 'auto',
};

const noteTypesLabelStyle = {
  fontSize: '0.75rem',
  fontWeight: '500',
  marginBottom: 0,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  flexShrink: 0,
};

const ComplianceGuidelines = () => {
  const { apiClient } = useContext(GlobalContext);
  // "Scorecard" when the scorecard flag is on, "Compliance" otherwise. Only the
  // user-facing copy changes — route hashes, GA event names and API calls keep
  // their existing "compliance" identifiers.
  const surfaceLabel = getScoreSurfaceLabel(apiClient);
  const surfaceLabelLower = surfaceLabel.toLowerCase();
  const location = useLocation();
  const navigate = useNavigate();
  const prevLocationRef = useRef(location);
  
  // Guideline list state
  const [guidelines, setGuidelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [duplicatingDefault, setDuplicatingDefault] = useState(false);
  
  // Upload modal state
  const [uploadModal, setUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [noteType, setNoteType] = useState('');
  const [title, setTitle] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  
  // Progress modal state
  const [progressModalOpen, setProgressModalOpen] = useState(false);
  const [progressText, setProgressText] = useState('');
  const [progressPercentage, setProgressPercentage] = useState(0);
  
  // Criteria viewing/editing state
  const [currentGuideline, setCurrentGuideline] = useState(null);
  const [viewingCriteria, setViewingCriteria] = useState(false);
  const [expandedSections, setExpandedSections] = useState([]);
  const [localCriteria, setLocalCriteria] = useState({});
  const [initialLocalCriteria, setInitialLocalCriteria] = useState({});
  const [expandedDocument, setExpandedDocument] = useState(null);
  
  // Track if we've already processed the guidelineId from URL
  const processedGuidelineIdRef = useRef(null);
  
  // Note type assignment state
  const [noteTypeAssignments, setNoteTypeAssignments] = useState({});
  const [initialNoteTypeAssignments, setInitialNoteTypeAssignments] = useState({});
  const [groupNoteTypes, setGroupNoteTypes] = useState([]);
  const [cachedApiNoteTypes, setCachedApiNoteTypes] = useState(null);
  const [currentStructureType, setCurrentStructureType] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Confirmation modal state
  const [deleteCategoryModal, setDeleteCategoryModal] = useState({ isOpen: false, categoryIndex: null, categoryName: '', requirementCount: 0 });
  const [deleteRequirementModal, setDeleteRequirementModal] = useState({ isOpen: false, categoryIndex: null, requirementIndex: null });
  const [unsavedChangesModal, setUnsavedChangesModal] = useState({
    isOpen: false,
    onConfirm: null,
    onClose: null,
  });
  
  // Dropdown state for note types
  const [noteTypeDropdowns, setNoteTypeDropdowns] = useState({});
  const isMountedRef = useRef(true);

  // ========== HELPER FUNCTIONS FOR STRUCTURE DETECTION AND MIGRATION ==========
  
  /**
   * Detects if criteria uses old structure (array of requirements) or new structure (object with category_note_types and requirements)
   * @param {Object} criteria - The criteria object to check
   * @returns {boolean} - true if old structure, false if new structure
   */
  const isOldStructure = (criteria) => {
    if (!criteria || typeof criteria !== 'object') return false;
    
    // Check if any category has an array (old structure)
    for (const categoryName in criteria) {
      if (Array.isArray(criteria[categoryName])) {
        return true;
      }
      // New structure has category_note_types and requirements
      if (criteria[categoryName] && typeof criteria[categoryName] === 'object' && 
          ('category_note_types' in criteria[categoryName] || 'requirements' in criteria[categoryName])) {
        return false;
      }
    }
    // Default to old structure if we can't determine (backward compatibility)
    return true;
  };

  /**
   * Creates default note type assignments object with all note types set to false (OFF)
   * @param {Array<string>} noteTypes - Array of note type names
   * @returns {Object} - Object with all note types set to false
   */
  const createDefaultNoteTypesOff = (noteTypes) => {
    const defaultObj = {};
    noteTypes.forEach((noteType) => {
      defaultObj[noteType] = false;
    });
    return defaultObj;
  };

  /**
   * Initializes note type assignments for old structure - all note types OFF by default
   * @param {Object} localCriteria - The criteria object
   * @param {Array<string>} noteTypes - Array of note type names
   * @returns {Object} - noteTypeAssignments object
   */
  const initializeNoteTypeAssignmentsForOldStructure = (localCriteria, noteTypes) => {
    const assignments = {};
    const defaultNoteTypes = createDefaultNoteTypesOff(noteTypes);
    
    Object.entries(localCriteria).forEach(([sectionName, criteria], categoryIndex) => {
      // Initialize for category
      assignments[categoryIndex] = { ...defaultNoteTypes };
      
      // Initialize for requirements
      if (Array.isArray(criteria)) {
        criteria.forEach((_, requirementIndex) => {
          const requirementKey = `${categoryIndex}-${requirementIndex}`;
          assignments[requirementKey] = { ...defaultNoteTypes };
        });
      }
    });
    
    return assignments;
  };

  /**
   * Merges saved note types with API note types
   * - Preserves note types in saved structure but not in API
   * - Adds new note types from API as OFF
   * @param {Object|Array} savedNoteTypes - Saved note types (can be object or array for backward compatibility)
   * @param {Array<string>} apiNoteTypes - Note types from getGroupNoteTypes API
   * @returns {Object} - Merged note types object with booleans
   */
  const mergeNoteTypes = (savedNoteTypes, apiNoteTypes) => {
    const merged = {};
    const normalizedApi = normalizeNoteTypeArray(apiNoteTypes || []);

    // Start with all API note types as OFF
    normalizedApi.forEach((noteType) => {
      merged[noteType] = false;
    });

    // If savedNoteTypes is an object (new structure), merge it
    if (savedNoteTypes && typeof savedNoteTypes === 'object' && !Array.isArray(savedNoteTypes)) {
      const normalizedSaved = normalizeNoteTypeObject(savedNoteTypes);
      Object.entries(normalizedSaved).forEach(([noteType, isEnabled]) => {
        merged[noteType] = isEnabled;
      });
    } 
    // If savedNoteTypes is an array (old structure for backward compatibility), mark those as enabled
    else if (Array.isArray(savedNoteTypes)) {
      normalizeNoteTypeArray(savedNoteTypes).forEach((noteType) => {
        if (typeof noteType === 'string') {
          merged[noteType] = true;
        }
      });
    }
    
    return merged;
  };

  /**
   * Loads note type assignments from new structure criteria
   * Merges saved note types with API note types (preserves saved, adds new as OFF)
   * @param {Object} localCriteria - The criteria object (new structure)
   * @param {Array<string>} apiNoteTypes - Array of note type names from API
   * @returns {Object} - noteTypeAssignments object
   */
  const loadNoteTypeAssignmentsFromNewStructure = (localCriteria, apiNoteTypes) => {
    const assignments = {};
    
    Object.entries(localCriteria).forEach(([sectionName, categoryData], categoryIndex) => {
      // Load category note types - merge saved with API
      if (categoryData && categoryData.category_note_types) {
        assignments[categoryIndex] = mergeNoteTypes(categoryData.category_note_types, apiNoteTypes);
      } else {
        // No saved note types, create defaults with all API note types OFF
        assignments[categoryIndex] = createDefaultNoteTypesOff(apiNoteTypes);
      }
      
      // Load requirement note types - merge saved with API
      if (categoryData && Array.isArray(categoryData.requirements)) {
        categoryData.requirements.forEach((requirement, requirementIndex) => {
          const requirementKey = `${categoryIndex}-${requirementIndex}`;
          if (requirement.note_types) {
            assignments[requirementKey] = mergeNoteTypes(requirement.note_types, apiNoteTypes);
          } else {
            // No saved note types, create defaults with all API note types OFF
            assignments[requirementKey] = createDefaultNoteTypesOff(apiNoteTypes);
          }
        });
      }
    });
    
    return assignments;
  };

  /**
   * Migrates old structure to new structure
   * @param {Object} localCriteria - Old structure criteria
   * @param {Object} noteTypeAssignments - Current note type assignments
   * @param {Array<string>} noteTypes - Array of note type names
   * @returns {Object} - { success: boolean, criteria: Object, errors: Array<string> }
   */
  const migrateToNewStructure = (localCriteria, noteTypeAssignments, noteTypes) => {
    const newCriteria = {};
    const errors = [];
    const categoryErrors = new Set(); // Track categories that already have errors
    
    Object.entries(localCriteria).forEach(([sectionName, criteria], categoryIndex) => {
      // Get category note types - save ALL note types as object with booleans
      const categoryNoteTypes = noteTypeAssignments[categoryIndex] || createDefaultNoteTypesOff(noteTypes);
      const categoryNoteTypesObj = {};
      noteTypes.forEach((noteType) => {
        categoryNoteTypesObj[noteType] = categoryNoteTypes[noteType] || false;
      });
      
      // Check if at least one is enabled
      const hasActiveNoteType = noteTypes.some((noteType) => categoryNoteTypes[noteType]);
      if (!hasActiveNoteType) {
        errors.push(`Category: ${sectionName} - Select at least 1 note type`);
        categoryErrors.add(sectionName);
      }
      
      // Convert requirements
      const requirements = [];
      if (Array.isArray(criteria)) {
        criteria.forEach((requirement, requirementIndex) => {
          const requirementKey = `${categoryIndex}-${requirementIndex}`;
          const reqNoteTypes = noteTypeAssignments[requirementKey] || createDefaultNoteTypesOff(noteTypes);
          
          // Save ALL note types as object with booleans
          const reqNoteTypesObj = {};
          noteTypes.forEach((noteType) => {
            reqNoteTypesObj[noteType] = reqNoteTypes[noteType] || false;
          });
          
          // Only add requirement error if category doesn't already have an error
          const hasActiveReqNoteType = noteTypes.some((noteType) => reqNoteTypes[noteType]);
          if (!hasActiveReqNoteType && !categoryErrors.has(sectionName)) {
            errors.push(`Category: ${sectionName} - Select at least 1 note type`);
            categoryErrors.add(sectionName);
          }
          
          // Convert weight to required (>=8 = true, <8 = false, missing = false)
          const weight = requirement.weight;
          const required = weight !== undefined && weight !== null && weight >= WEIGHT_REQUIRED;
          
          requirements.push({
            requirement_text: requirement.requirement_text || '',
            required: required,
            note_types: reqNoteTypesObj,
            use_patient_history: requirement.use_patient_history === true,
          });
        });
      }
      
      newCriteria[sectionName] = {
        category_note_types: categoryNoteTypesObj,
        requirements: requirements,
      };
    });
    
    return {
      success: errors.length === 0,
      criteria: newCriteria,
      errors: errors,
    };
  };

  /**
   * Checks if there are unsaved changes in note type assignments or criteria
   * @param {Object} initialAssignments - Initial note type assignments
   * @param {Object} currentAssignments - Current note type assignments
   * @param {Object} initialCriteria - Initial criteria
   * @param {Object} currentCriteria - Current criteria
   * @returns {boolean} - true if there are unsaved changes
   */
  const hasUnsavedChanges = (initialAssignments, currentAssignments, initialCriteria, currentCriteria) => {
    const assignmentsChanged = JSON.stringify(initialAssignments) !== JSON.stringify(currentAssignments);
    const criteriaChanged = JSON.stringify(initialCriteria) !== JSON.stringify(currentCriteria);
    return assignmentsChanged || criteriaChanged;
  };

  // Memoized flag to track if there are unsaved edits
  const hasPendingChanges = useMemo(
    () =>
      hasUnsavedChanges(
        initialNoteTypeAssignments,
        noteTypeAssignments,
        initialLocalCriteria,
        localCriteria,
      ),
    [
      initialNoteTypeAssignments,
      noteTypeAssignments,
      initialLocalCriteria,
      localCriteria,
    ],
  );

  /**
   * Fetches group note types from API, updates state, and caches the result
   * @returns {Promise<Array<string>|null>} - Array of note types or null if failed
   */
  const fetchAndSetGroupNoteTypes = async () => {
    // Check cache first
    if (cachedApiNoteTypes) {
      const normalized = normalizeNoteTypeArray(cachedApiNoteTypes);
      setGroupNoteTypes(normalized);
      return normalized;
    }
    
    try {
      const response = await apiClient.getGroupNoteTypes();
      if (response.noteTypes && Array.isArray(response.noteTypes) && response.noteTypes.length > 0) {
        const normalized = normalizeNoteTypeArray(response.noteTypes);
        // Cache the API response for future old structure views
        setCachedApiNoteTypes(normalized);
        setGroupNoteTypes(normalized);
        return normalized;
      } else {
        setGroupNoteTypes([]);
        return null;
      }
    } catch (error) {
      console.error('Error fetching group note types:', error);
      setGroupNoteTypes([]);
      return null;
    }
  };

  /**
   * Extracts all unique note types from new structure criteria
   * Handles both old array format and new object format for backward compatibility
   * @param {Object} criteria - The criteria object (new structure)
   * @returns {Array<string>} - Array of unique note type names
   */
  const extractNoteTypesFromCriteria = (criteria) => {
    const noteTypesSet = new Set();
    
    Object.values(criteria).forEach((categoryData) => {
      if (categoryData && typeof categoryData === 'object') {
        // Extract from category_note_types
        if (categoryData.category_note_types) {
          // Handle new object format: { "Daily": true, "Progress": false, ... }
          if (typeof categoryData.category_note_types === 'object' && !Array.isArray(categoryData.category_note_types)) {
            Object.keys(categoryData.category_note_types).forEach((noteType) => {
              const normalized = normalizeNoteType(noteType);
              if (normalized) noteTypesSet.add(normalized);
            });
          }
          // Handle old array format: ["Daily", "Progress"] (backward compatibility)
          else if (Array.isArray(categoryData.category_note_types)) {
            categoryData.category_note_types.forEach((noteType) => {
              const normalized = normalizeNoteType(noteType);
              if (normalized) noteTypesSet.add(normalized);
            });
          }
        }
        
        // Extract from requirement note_types
        if (Array.isArray(categoryData.requirements)) {
          categoryData.requirements.forEach((requirement) => {
            if (requirement.note_types) {
              // Handle new object format: { "Daily": true, "Progress": false, ... }
              if (typeof requirement.note_types === 'object' && !Array.isArray(requirement.note_types)) {
                Object.keys(requirement.note_types).forEach((noteType) => {
                  const normalized = normalizeNoteType(noteType);
                  if (normalized) noteTypesSet.add(normalized);
                });
              }
              // Handle old array format: ["Daily", "Progress"] (backward compatibility)
              else if (Array.isArray(requirement.note_types)) {
                requirement.note_types.forEach((noteType) => {
                  const normalized = normalizeNoteType(noteType);
                  if (normalized) noteTypesSet.add(normalized);
                });
              }
            }
          });
        }
      }
    });
    
    return Array.from(noteTypesSet);
  };

  /**
   * Initializes note type assignments based on criteria structure
   * Resets note types when switching between old/new structures, uses cache for old structures
   * For new structures, merges saved note types with API note types (preserves saved, adds new as OFF)
   * @param {Object} criteria - The criteria object
   * @returns {Promise<Object>} - noteTypeAssignments object
   */
  const initializeNoteTypeAssignments = async (criteria) => {
    const isOld = isOldStructure(criteria);
    const structureType = isOld ? STRUCTURE_TYPE_OLD : STRUCTURE_TYPE_NEW;
    const isSwitchingStructure = currentStructureType !== null && currentStructureType !== structureType;
    
    let assignments = {};
    let noteTypes = [];
    
    if (isOld) {
      // Old structure: use cached API response if available, otherwise call API
      if (isSwitchingStructure) {
        resetNoteTypesState();
      }
      
      noteTypes = await fetchAndSetGroupNoteTypes();
      if (noteTypes) {
        assignments = initializeNoteTypeAssignmentsForOldStructure(criteria, noteTypes);
      }
    } else {
      // New structure: merge saved note types with API note types
      if (isSwitchingStructure) {
        resetNoteTypesState();
      }
      
      // Extract saved note types from criteria
      const savedNoteTypes = extractNoteTypesFromCriteria(criteria);
      
      // Fetch API note types to merge with saved ones
      const apiNoteTypes = await fetchAndSetGroupNoteTypes();
      
      // Merge: combine saved note types with API note types
      // This ensures we preserve saved note types that aren't in API, and add new API note types as OFF
      const mergedNoteTypes = [
        ...new Set([
          ...normalizeNoteTypeArray(savedNoteTypes),
          ...(apiNoteTypes || []),
        ]),
      ];
      setGroupNoteTypes(mergedNoteTypes);
      
      // Load assignments, which will merge saved with API
      assignments = loadNoteTypeAssignmentsFromNewStructure(criteria, mergedNoteTypes);
    }
    
    setCurrentStructureType(structureType);
    setNoteTypeAssignments(assignments);
    setInitialNoteTypeAssignments(JSON.parse(JSON.stringify(assignments)));
    return assignments;
  };

  /**
   * Resets note types state (used when switching between old/new structures)
   */
  const resetNoteTypesState = () => {
    setGroupNoteTypes([]);
  };

  /**
   * Resets all criteria-related state
   */
  const resetCriteriaState = () => {
    setViewingCriteria(false);
    setExpandedDocument(null);
    setCurrentGuideline(null);
    setLocalCriteria({});
    setInitialLocalCriteria({});
    setNoteTypeAssignments({});
    setInitialNoteTypeAssignments({});
    setExpandedSections([]);
    setCurrentStructureType(null);
  };

  /**
   * Handles close action with unsaved changes check
   */
  const handleCloseWithUnsavedCheck = () => {
    if (hasPendingChanges) {
      setUnsavedChangesModal({
        isOpen: true,
        onConfirm: () => {
          approveGuideline();
          setUnsavedChangesModal({ isOpen: false, onConfirm: null, onClose: null });
        },
        onClose: handleDiscardChanges,
      });
    } else {
      resetCriteriaState();
    }
  };

  /**
   * Gets requirements array from criteria (handles both old and new structures)
   * @param {Object|Array} criteria - The criteria (can be array or object with requirements)
   * @returns {Array} - Array of requirements
   */
  const getRequirementsFromCriteria = (criteria) => {
    if (Array.isArray(criteria)) {
      return criteria;
    }
    if (criteria?.requirements && Array.isArray(criteria.requirements)) {
      return criteria.requirements;
    }
    return [];
  };

  /**
   * Gets section name from category index
   * @param {Object} criteria - The criteria object
   * @param {number} categoryIndex - Index of the category
   * @returns {string|null} - Section name or null if not found
   */
  const getSectionNameByIndex = (criteria, categoryIndex) => {
    const sections = Object.keys(criteria);
    return sections[categoryIndex] || null;
  };

  /**
   * Updates a requirement in criteria (handles both old and new structures)
   * @param {Object} criteria - The category criteria (can be array or object)
   * @param {number} requirementIndex - Index of the requirement to update
   * @param {Function} updateFn - Function that receives the requirement and returns updated requirement
   * @returns {Object|Array} - Updated criteria
   */
  const updateRequirementInCriteria = (criteria, requirementIndex, updateFn) => {
    if (Array.isArray(criteria)) {
      // Old structure
      const updatedRequirements = [...criteria];
      if (updatedRequirements[requirementIndex]) {
        updatedRequirements[requirementIndex] = updateFn(updatedRequirements[requirementIndex]);
      }
      return updatedRequirements;
    } else if (criteria?.requirements && Array.isArray(criteria.requirements)) {
      // New structure
      const updatedRequirements = [...criteria.requirements];
      if (updatedRequirements[requirementIndex]) {
        updatedRequirements[requirementIndex] = updateFn(updatedRequirements[requirementIndex]);
      }
      return {
        ...criteria,
        requirements: updatedRequirements,
      };
    }
    return criteria;
  };

  // Status messages for guidelines extraction
  const COMPLIANCE_EXTRACTION_STATUS = [
    `Processing your ${surfaceLabelLower} document...`,
    `Analyzing ${surfaceLabelLower} requirements...`,
    'Extracting key criteria and guidelines...',
    `Finalizing ${surfaceLabelLower} analysis...`,
    `Almost done with your ${surfaceLabelLower} guidelines!`,
  ];

  // Helper function to initialize progress modal
  const initializeProgressModal = () => {
    setProgressPercentage(0);
    setProgressText(`${COMPLIANCE_EXTRACTION_STATUS[0]} (5%)`);
    setProgressModalOpen(true);
  };

  // Helper function to create progress update loop
  const createProgressUpdater = () => {
    let currentStatusIndex = 0;
    let lastProgressUpdate = 0;
    let currentProgress = 5;

    return async () => {
      while (currentProgress < 90) {
        currentProgress = Math.min(currentProgress + 20, 90);
        setProgressPercentage(currentProgress);

        if (currentProgress !== lastProgressUpdate) {
          lastProgressUpdate = currentProgress;
          setProgressText(
            `${COMPLIANCE_EXTRACTION_STATUS[currentStatusIndex]} (${currentProgress}%)`,
          );
          currentStatusIndex =
            (currentStatusIndex + 1) % COMPLIANCE_EXTRACTION_STATUS.length;
        }
        await sleep(4000);
      }
    };
  };

  // Helper function to handle successful extraction
  const handleExtractionSuccess = async (
    response,
    guidelineId,
    progressPromise,
  ) => {
    await progressPromise;
    setProgressPercentage(100);
    setProgressText('Extraction completed successfully!');
    await sleep(1000);
    setProgressModalOpen(false);

    const criteria = normalizeCriteriaNoteTypes(response.criteria || {});
    setLocalCriteria(criteria);
    setInitialLocalCriteria(JSON.parse(JSON.stringify(criteria)));
    setCurrentGuideline({
      guideline_id: guidelineId,
      ...response,
    });
    setViewingCriteria(true);
    
    // Initialize note type assignments based on structure
    await initializeNoteTypeAssignments(criteria);

    const currentActiveDoc = getCurrentActiveDocument();
    const successMessage = getSuccessMessage(
      currentActiveDoc,
      guidelineId,
      response.document_name,
    );
    toast.success(successMessage);
  };

  // Helper function to get success message
  const getSuccessMessage = (currentActiveDoc, guidelineId, documentName) => {
    return currentActiveDoc && currentActiveDoc.guideline_id !== guidelineId
      ? `${documentName || 'New document'} is now your active ${surfaceLabelLower} document! Your previous document has been safely archived.`
      : `Document processed successfully! Please review the extracted criteria.`;
  };

  // Helper function to get viewing criteria title
  const getViewingCriteriaTitle = (currentGuideline) => {
    const documentName = currentGuideline?.document_name;
    return `👁️ Viewing ${surfaceLabel} Criteria for Document: ${documentName}`;
  };

  // Helper function to get badge color for guideline status
  const getGuidelineBadgeColor = (status) => {
    if (status === 'active') return 'success';
    return 'secondary';
  };

  // Helper function to get badge text for guideline status
  const getGuidelineBadgeText = (status) => {
    if (status === 'active') return '✅ Active';
    return '❌ Inactive';
  };

  // Helper function to get upload button text and color
  const getUploadButtonConfig = (uploading, currentActiveDoc) => {
    if (uploading) {
      return { text: 'Processing...', color: 'primary' };
    }
    if (currentActiveDoc) {
      return { text: 'Upload & Replace Current', color: 'warning' };
    }
    return { text: 'Upload & Process', color: 'primary' };
  };

  // Helper function to handle polling logic
  const startPolling = (guidelineId, maxPollingDuration) => {
    const pollingIntervalId = setInterval(async () => {
      try {
        const statusResponse =
          await apiClient.getComplianceGuidelineStatus(guidelineId);
        if (statusResponse?.status === 'archived') {
          clearInterval(pollingIntervalId);
          await handlePollingSuccess(statusResponse, guidelineId);
          return;
        }
      } catch (error) {
        console.error('Error polling compliance status:', error);
      }
    }, 10000);

    const pollingTimeoutId = setTimeout(() => {
      clearInterval(pollingIntervalId);
      setProgressModalOpen(false);
      toast.error(
        `${surfaceLabel} guidelines extraction timed out. Please try again.`,
      );
    }, maxPollingDuration);

    return () => {
      clearInterval(pollingIntervalId);
      clearTimeout(pollingTimeoutId);
    };
  };

  // Helper function to open a guideline in full-screen "View & Edit" mode
  const openGuidelineInViewEditMode = async (guideline) => {
    try {
      let criteria = normalizeCriteriaNoteTypes(guideline.criteria || {});
      
      // If criteria is empty or not available, fetch the full guideline data
      if (!criteria || Object.keys(criteria).length === 0) {
        const statusResponse = await apiClient.getComplianceGuidelineStatus(
          guideline.guideline_id,
        );
        if (statusResponse && statusResponse.criteria) {
          criteria = normalizeCriteriaNoteTypes(statusResponse.criteria);
        } else {
          toast.warning(
            'No criteria found for this guideline. It may not have been processed yet.',
          );
          return;
        }
      }

      setLocalCriteria(criteria);
      setInitialLocalCriteria(JSON.parse(JSON.stringify(criteria)));
      setCurrentGuideline(guideline);
      setViewingCriteria(true);
      
      // Initialize note type assignments based on structure
      await initializeNoteTypeAssignments(criteria);
    } catch (error) {
      console.error('Error opening guideline in View & Edit mode:', error);
      toast.error('Failed to open guideline');
    }
  };

  // Helper function to handle polling success
  const handlePollingSuccess = async (statusResponse, guidelineId) => {
    setProgressPercentage(100);
    setProgressText('Extraction completed successfully!');
    await sleep(1000);
    setProgressModalOpen(false);

    const criteria = normalizeCriteriaNoteTypes(statusResponse.criteria || {});
    setLocalCriteria(criteria);
    setInitialLocalCriteria(JSON.parse(JSON.stringify(criteria)));
    setCurrentGuideline({
      guideline_id: guidelineId,
      ...statusResponse,
    });
    setViewingCriteria(true);
    
    // Initialize note type assignments based on structure
    await initializeNoteTypeAssignments(criteria);

    const currentActiveDoc = getCurrentActiveDocument();
    const successMessage = getSuccessMessage(
      currentActiveDoc,
      guidelineId,
      statusResponse.document_name,
    );
    toast.success(successMessage);
  };

  // Sleep utility
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // Main polling function for compliance guidelines extraction with progress modal
  const runComplianceExtractionWithProgress = async (guidelineId, noteType) => {
    const maxPollingDuration = 3 * 60 * 1000; // 3 minutes max polling duration

    initializeProgressModal();
    const updateProgress = createProgressUpdater();
    const progressPromise = updateProgress();

    try {
      // Try initial extraction with timeout
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(
          () => reject(new Error('Request timeout after 30 seconds')),
          30000,
        );
      });

      const extractPromise = apiClient.extractComplianceGuidelines(
        guidelineId,
        noteType,
      );
      const response = await Promise.race([extractPromise, timeoutPromise]);

      await handleExtractionSuccess(response, guidelineId, progressPromise);
      return response;
    } catch (error) {
      console.log('Initial extraction timed out, starting polling...');
      setProgressText(
        'Extraction is taking longer than expected. Checking status...',
      );
      return startPolling(guidelineId, maxPollingDuration);
    }
  };

  // Fetch group note types on mount
  const fetchGroupNoteTypes = async () => {
    try {
      const response = await apiClient.getGroupNoteTypes();
      if (response.noteTypes && Array.isArray(response.noteTypes) && response.noteTypes.length > 0) {
        setGroupNoteTypes(response.noteTypes);
      }
    } catch (error) {
      console.error('Error fetching group note types:', error);
      // Keep default values if API fails
    }
  };

  useEffect(() => {
    fetchGuidelines();
    // Note: getGroupNoteTypes is called lazily when viewing a guideline if not already cached
  }, []);

  // Handles discard changes confirmation
  const handleDiscardChanges = () => {
    resetCriteriaState();
    setUnsavedChangesModal({ isOpen: false, onConfirm: null, onClose: null });
  };

  // Prevent closing tab / browser navigation when there are unsaved edits
  useBeforeUnload((event) => {
    if (!hasPendingChanges) return;
    event.preventDefault();
    event.returnValue = '';
  });

  // Auto-open guideline in "View & Edit" mode if guidelineId is in URL
  useEffect(() => {
    // Only proceed if guidelines are loaded and not already viewing
    if (loading || viewingCriteria || guidelines.length === 0) {
      return;
    }

    // Check both hash and search for guidelineId parameter
    const hash = location.hash || '';
    const search = location.search || '';
    const hashParams = hash.includes('?') ? new URLSearchParams(hash.split('?')[1]) : null;
    const searchParams = new URLSearchParams(search);
    const guidelineIdParam = hashParams?.get('guidelineId') || searchParams.get('guidelineId');
    
    if (guidelineIdParam) {
      // Convert to string for comparison (guideline_id might be stored as number or string)
      const guidelineId = String(guidelineIdParam);
      
      // Skip if we've already processed this guidelineId
      if (processedGuidelineIdRef.current === guidelineId) {
        return;
      }
      
      const guideline = guidelines.find(g => String(g.guideline_id) === guidelineId);
      
      if (guideline) {
        console.log('Opening guideline in View & Edit mode:', guideline);
        processedGuidelineIdRef.current = guidelineId; // Mark as processed
        openGuidelineInViewEditMode(guideline);
        // Clear the URL parameter after opening (keep just the hash)
        const baseHash = hash.split('?')[0] || '#compliance';
        navigate(location.pathname + baseHash, { replace: true });
      } else {
        console.log('Guideline not found:', guidelineId, 'Available guidelines:', guidelines.map(g => g.guideline_id));
      }
    } else {
      // Reset processed ref when there's no guidelineId in URL
      processedGuidelineIdRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.hash, guidelines, viewingCriteria, loading]);


  const fetchGuidelines = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getComplianceGuidelines();
      if (response.guidelines) {
        setGuidelines(response.guidelines);
      }
    } catch (error) {
      console.error('Error fetching guidelines:', error);
      toast.error(`Failed to load ${surfaceLabelLower} guidelines`);
    } finally {
      setLoading(false);
    }
  };

  // Create a copy of the default CMS guideline for this group
  const handleDuplicateDefaultGuideline = async () => {
    if (duplicatingDefault) return;
    setDuplicatingDefault(true);
    try {
      const response = await apiClient.duplicateCmsGuideline();
      const newGuidelineId = response?.guidelineId || response?.guideline_id;
      if (!newGuidelineId) {
        toast.error('Could not duplicate the default guideline. Please try again.');
        return;
      }

      toast.success('CMS guideline copied. Opening it for editing...');

      // Refresh list and open the new guideline
      const listResponse = await apiClient.getComplianceGuidelines();
      if (listResponse?.guidelines) {
        setGuidelines(listResponse.guidelines);
        const newGuideline = listResponse.guidelines.find(
          (g) => String(g.guideline_id) === String(newGuidelineId),
        );
        if (newGuideline) {
          await openGuidelineInViewEditMode(newGuideline);
        }
      }
    } catch (error) {
      console.error('Error duplicating CMS guideline:', error);
      toast.error('Failed to duplicate CMS guideline');
    } finally {
      setDuplicatingDefault(false);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please upload a PDF, DOC, or DOCX file');
        return;
      }

      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }

      setSelectedFile(file);
    }
  };

  const getCurrentActiveDocument = () => {
    return guidelines.find((g) => g.status === 'active');
  };

  const processDocument = async () => {
    if (!selectedFile) {
      toast.error('Please select a file to upload');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Step 1: Get presigned URLs
      setUploadProgress(20);
      const urlResponse = await apiClient.getComplianceUrls([
        selectedFile.name,
      ]);

      if (urlResponse.error) {
        toast.error(urlResponse.error);
        return;
      }

      // Step 2: Upload file to S3
      setUploadProgress(40);
      const presignedUrl = urlResponse[selectedFile.name];
      const uploadResponse = await fetch(presignedUrl, {
        method: 'PUT',
        body: selectedFile,
        headers: {
          'Content-Type': selectedFile.type,
        },
      });
      console.log('Upload to S3');
      console.log(uploadResponse);
      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file to S3');
      }
      logGAEvent('compliance_document_uploaded', apiClient);
      // Step 3: Add document URLs to guideline record
      setUploadProgress(60);
      const cleanUrl = presignedUrl.split('?')[0]; // Remove query params
      const addResponse = await apiClient.addComplianceDocuments(
        noteType || 'General',
        [cleanUrl],
        title || selectedFile.name,
      );

      if (addResponse.error) {
        toast.error(addResponse.error);
        return;
      }

      // Step 4: Extract criteria with polling
      setUploadProgress(80);
      setUploadModal(false); // Close upload modal

      // Start the extraction process with polling
      await runComplianceExtractionWithProgress(
        addResponse.guidelineId,
        noteType || null,
      );

      setUploadProgress(100);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to process document. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Handler functions for editing criteria (local state only) - Memoized to prevent re-renders
  const handleSectionNameChange = useCallback((index, newName) => {
    setLocalCriteria((prevCriteria) => {
      const entries = Object.entries(prevCriteria);
      const newCriteria = {};

      entries.forEach(([sectionName, sectionCriteria], idx) => {
        const keyToUse = idx === index ? newName : sectionName;
        newCriteria[keyToUse] = sectionCriteria;
      });

      return newCriteria;
    });
  }, []);

  // Handler for requirement text changes (handles both old and new structures)
  const handleRequirementTextChange = useCallback(
    (categoryIndex, requirementIndex, newText) => {
      setLocalCriteria((prevCriteria) => {
        const sectionName = getSectionNameByIndex(prevCriteria, categoryIndex);
        if (!sectionName) return prevCriteria;

        const updatedCriteria = { ...prevCriteria };
        const categoryData = updatedCriteria[sectionName];
        
        updatedCriteria[sectionName] = updateRequirementInCriteria(
          categoryData,
          requirementIndex,
          (requirement) => ({
            ...requirement,
            requirement_text: newText,
          })
        );
        
        return updatedCriteria;
      });
    },
    [],
  );

  const handleRequirementRequiredChange = useCallback(
    (categoryIndex, requirementIndex, isRequired) => {
      setLocalCriteria((prevCriteria) => {
        const sectionName = getSectionNameByIndex(prevCriteria, categoryIndex);
        if (!sectionName) return prevCriteria;

        const updatedCriteria = { ...prevCriteria };
        const categoryData = updatedCriteria[sectionName];
        
        updatedCriteria[sectionName] = updateRequirementInCriteria(
          categoryData,
          requirementIndex,
          (requirement) => {
            // Old structure: convert required to weight
            if (Array.isArray(categoryData)) {
              return {
                ...requirement,
                weight: isRequired ? WEIGHT_REQUIRED : WEIGHT_OPTIONAL,
              };
            }
            // New structure: use required boolean
            return {
              ...requirement,
              required: isRequired,
            };
          }
        );
        
        return updatedCriteria;
      });
    },
    [],
  );

  // Grade this requirement against the patient's prior visits, not just the
  // note being scored (same look-back window the patient chat uses).
  const handleRequirementUseHistoryChange = useCallback(
    (categoryIndex, requirementIndex, usePatientHistory) => {
      setLocalCriteria((prevCriteria) => {
        const sectionName = getSectionNameByIndex(prevCriteria, categoryIndex);
        if (!sectionName) return prevCriteria;

        const updatedCriteria = { ...prevCriteria };
        const categoryData = updatedCriteria[sectionName];

        updatedCriteria[sectionName] = updateRequirementInCriteria(
          categoryData,
          requirementIndex,
          (requirement) => ({
            ...requirement,
            use_patient_history: usePatientHistory,
          }),
        );

        return updatedCriteria;
      });
    },
    [],
  );

  // Add new category
  const handleAddCategory = useCallback(() => {
    const newCategoryName = `Category ${Object.keys(localCriteria).length + 1}`;
    const defaultNoteTypes = createDefaultNoteTypesOff(groupNoteTypes);
    
    setLocalCriteria((prev) => ({
      ...prev,
      [newCategoryName]: {
        category_note_types: defaultNoteTypes,
        requirements: [],
      },
    }));
    
    const categoryIndex = Object.keys(localCriteria).length;
    setNoteTypeAssignments((prev) => ({
      ...prev,
      [categoryIndex]: defaultNoteTypes,
    }));
    setInitialNoteTypeAssignments((prev) => ({
      ...prev,
      [categoryIndex]: defaultNoteTypes,
    }));
  }, [localCriteria, groupNoteTypes]);

  // Delete category
  const handleDeleteCategory = useCallback((categoryIndex) => {
    const sectionName = getSectionNameByIndex(localCriteria, categoryIndex);
    if (!sectionName) return;
    
    const categoryData = localCriteria[sectionName];
    const requirements = getRequirementsFromCriteria(categoryData);
    const requirementCount = requirements.length;
    
    setDeleteCategoryModal({
      isOpen: true,
      categoryIndex,
      categoryName: sectionName,
      requirementCount,
    });
  }, [localCriteria]);

  const confirmDeleteCategory = useCallback(() => {
    const { categoryIndex, categoryName } = deleteCategoryModal;
    if (categoryIndex === null || !categoryName) return;
    
    setLocalCriteria((prev) => {
      const updated = { ...prev };
      delete updated[categoryName];
      return updated;
    });
    
    // Remove note type assignments for this category
    setNoteTypeAssignments((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((key) => {
        if (key === String(categoryIndex) || key.startsWith(`${categoryIndex}-`)) {
          delete updated[key];
        }
      });
      return updated;
    });
    setInitialNoteTypeAssignments((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((key) => {
        if (key === String(categoryIndex) || key.startsWith(`${categoryIndex}-`)) {
          delete updated[key];
        }
      });
      return updated;
    });
    
    setDeleteCategoryModal({ isOpen: false, categoryIndex: null, categoryName: '', requirementCount: 0 });
  }, [deleteCategoryModal]);

  // Add requirement
  const handleAddRequirement = useCallback((categoryIndex) => {
    const sectionName = getSectionNameByIndex(localCriteria, categoryIndex);
    if (!sectionName) return;
    
    // Inherit category's note types (enabled ones should be enabled for new requirement)
    const categoryNoteTypes = noteTypeAssignments[categoryIndex] || createDefaultNoteTypesOff(groupNoteTypes);
    const requirementNoteTypes = { ...categoryNoteTypes };
    
    // Convert to object format for saving
    const noteTypesObj = {};
    groupNoteTypes.forEach((noteType) => {
      noteTypesObj[noteType] = requirementNoteTypes[noteType] || false;
    });
    
    const newRequirement = {
      requirement_text: '',
      required: false,
      note_types: noteTypesObj,
    };
    
    setLocalCriteria((prev) => ({
      ...prev,
      [sectionName]: {
        ...prev[sectionName],
        requirements: [...(prev[sectionName].requirements || []), newRequirement],
      },
    }));
    
    const requirementIndex = (localCriteria[sectionName].requirements || []).length;
    const requirementKey = `${categoryIndex}-${requirementIndex}`;
    setNoteTypeAssignments((prev) => ({
      ...prev,
      [requirementKey]: requirementNoteTypes,
    }));
    setInitialNoteTypeAssignments((prev) => ({
      ...prev,
      [requirementKey]: requirementNoteTypes,
    }));
  }, [localCriteria, groupNoteTypes, noteTypeAssignments]);

  // Delete requirement
  const handleDeleteRequirement = useCallback((categoryIndex, requirementIndex) => {
    setDeleteRequirementModal({
      isOpen: true,
      categoryIndex,
      requirementIndex,
    });
  }, []);

  const confirmDeleteRequirement = useCallback(() => {
    const { categoryIndex, requirementIndex } = deleteRequirementModal;
    if (categoryIndex === null || requirementIndex === null) return;
    
    const sectionName = getSectionNameByIndex(localCriteria, categoryIndex);
    if (!sectionName) return;
    
    setLocalCriteria((prev) => ({
      ...prev,
      [sectionName]: {
        ...prev[sectionName],
        requirements: (prev[sectionName].requirements || []).filter((_, idx) => idx !== requirementIndex),
      },
    }));
    
    const requirementKey = `${categoryIndex}-${requirementIndex}`;
    setNoteTypeAssignments((prev) => {
      const updated = { ...prev };
      delete updated[requirementKey];
      return updated;
    });
    setInitialNoteTypeAssignments((prev) => {
      const updated = { ...prev };
      delete updated[requirementKey];
      return updated;
    });
    
    setDeleteRequirementModal({ isOpen: false, categoryIndex: null, requirementIndex: null });
  }, [deleteRequirementModal, localCriteria]);

  const approveGuideline = async (options = {}) => {
    const { silent = false, skipReset = false } = options;
    if (!currentGuideline) return;

    // Check if note types are loaded
    if (groupNoteTypes.length === 0) {
      alert('Unable to load note types. Please refresh the page and try again.'); // eslint-disable-line no-undef
      return;
    }

    let processedCriteria = { ...localCriteria };

    // Check if old structure - if so, migrate to new structure
    if (isOldStructure(localCriteria)) {
      // Migrate old structure to new structure
      const migrationResult = migrateToNewStructure(localCriteria, noteTypeAssignments, groupNoteTypes);
      
      if (!migrationResult.success) {
        // Show validation errors (simplified format)
        const errorMessage = migrationResult.errors.join('\n');
        alert(errorMessage); // eslint-disable-line no-undef
        return;
      }
      
      processedCriteria = migrationResult.criteria;
    } else {
      // New structure: merge noteTypeAssignments into criteria
      // Save ALL note types as objects with booleans (not just active ones)
      Object.entries(localCriteria).forEach(([sectionName, categoryData], categoryIndex) => {
        if (categoryData && typeof categoryData === 'object') {
          // Update category note types - save ALL note types as object with booleans
          const categoryNoteTypes = noteTypeAssignments[categoryIndex] || createDefaultNoteTypesOff(groupNoteTypes);
          const categoryNoteTypesObj = {};
          groupNoteTypes.forEach((noteType) => {
            categoryNoteTypesObj[noteType] = categoryNoteTypes[noteType] || false;
          });
          
          // Update requirement note types - save ALL note types as object with booleans
          if (Array.isArray(categoryData.requirements)) {
            categoryData.requirements.forEach((requirement, requirementIndex) => {
              const requirementKey = `${categoryIndex}-${requirementIndex}`;
              const reqNoteTypes = noteTypeAssignments[requirementKey] || createDefaultNoteTypesOff(groupNoteTypes);
              
              const reqNoteTypesObj = {};
              groupNoteTypes.forEach((noteType) => {
                reqNoteTypesObj[noteType] = reqNoteTypes[noteType] || false;
              });
              
              requirement.note_types = reqNoteTypesObj;
            });
          }
          
          processedCriteria[sectionName] = {
            ...categoryData,
            category_note_types: categoryNoteTypesObj,
          };
        }
      });
    }

    setIsSaving(true);
    try {
      // Use the processed criteria for the update
      await apiClient.updateComplianceCriteria(
        currentGuideline.guideline_id,
        processedCriteria,
      );
      
      // Sync state post-save:
      // - only update localCriteria when we stay on the page (skipReset) to avoid re-render loops
      // - always refresh the "initial" snapshots so the dirty banner clears
      if (skipReset && isMountedRef.current) {
        setLocalCriteria(processedCriteria);
      }
      setInitialLocalCriteria(JSON.parse(JSON.stringify(processedCriteria)));
      setInitialNoteTypeAssignments(
        JSON.parse(JSON.stringify(noteTypeAssignments)),
      );

      if (!silent) {
        toast.success(`${surfaceLabel} guidelines updated successfully!`);
      }

      // Close both inline and full-screen views (only if component still mounted and not skipped)
      if (!skipReset && isMountedRef.current) {
        resetCriteriaState();
        fetchGuidelines();
      }
    } catch (error) {
      console.error('Error updating guidelines:', error);
      if (!silent) {
        toast.error('Failed to update guidelines');
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Keep latest values in refs so the unmount handler doesn't re-run on every change
  const latestPendingChangesRef = useRef(false);
  const latestGuidelineRef = useRef(null);
  const approveGuidelineRef = useRef(approveGuideline);

  useEffect(() => {
    latestPendingChangesRef.current = hasPendingChanges;
  }, [hasPendingChanges]);

  useEffect(() => {
    latestGuidelineRef.current = currentGuideline;
  }, [currentGuideline]);

  useEffect(() => {
    approveGuidelineRef.current = approveGuideline;
  }, [approveGuideline]);

  // Auto-save only on unmount (e.g., user clicks away/side panel)
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (latestPendingChangesRef.current && latestGuidelineRef.current) {
        approveGuidelineRef.current?.({ silent: true, skipReset: true });
      }
    };
  }, []);


  // NOTE: React Router's navigation blocking requires Data Router.
  // We rely on beforeunload (tab/window) plus explicit close buttons.

  const viewGuideline = async (guideline) => {
    console.log('viewGuideline called with:', guideline);
    console.log('guideline.criteria:', guideline.criteria);

    try {
      // If criteria is empty or not available, fetch the full guideline data
      if (!guideline.criteria || Object.keys(guideline.criteria).length === 0) {
        console.log('No criteria found, fetching guideline status...');
        const statusResponse = await apiClient.getComplianceGuidelineStatus(
          guideline.guideline_id,
        );
        console.log('Status response:', statusResponse);

        if (statusResponse && statusResponse.criteria) {
          guideline.criteria = statusResponse.criteria;
        } else {
          toast.warning(
            'No criteria found for this guideline. It may not have been processed yet.',
          );
          return;
        }
      }

      // Check if this document is already expanded
      if (expandedDocument === guideline.guideline_id) {
        // Collapse if already expanded
        resetCriteriaState();
      } else {
        // Expand this document inline
        const criteria = guideline.criteria || {};
        setCurrentGuideline(guideline);
        setLocalCriteria(criteria);
        setInitialLocalCriteria(JSON.parse(JSON.stringify(criteria)));
        setExpandedSections([]);
        setExpandedDocument(guideline.guideline_id);
        
        // Initialize note type assignments based on structure
        await initializeNoteTypeAssignments(criteria);
      }

      // Close the old full-screen view if it was open
      setViewingCriteria(false);

      console.log('States set - expandedDocument:', guideline.guideline_id);
    } catch (error) {
      console.error('Error loading guideline:', error);
      toast.error('Failed to load guideline criteria');
    }
  };

  const activateGuideline = async (guideline) => {
    if (
      !window.confirm(
        // eslint-disable-line no-undef
        `Make "${guideline.document_name}" your active ${surfaceLabelLower} document?`,
      )
    ) {
      return;
    }

    try {
      await apiClient.activateComplianceGuideline(guideline.guideline_id);
      toast.success('Document activated successfully!');
      fetchGuidelines(); // Refresh the list
    } catch (error) {
      console.error('Error activating guideline:', error);
      toast.error('Failed to activate document');
    }
  };

  const deactivateActiveGuideline = async (guideline = null) => {
    try {
      await apiClient.deactivateComplianceGuideline(
        guideline?.guideline_id || null,
      );
      toast.success(
        `${surfaceLabel} guidelines disabled. Using default CMS criteria.`,
      );
      fetchGuidelines();
      // If we were viewing the now-disabled doc inline, collapse it
      resetCriteriaState();
    } catch (error) {
      console.error('Error deactivating guideline:', error);
      toast.error(`Failed to disable ${surfaceLabelLower} guidelines`);
    }
  };

  const resetUploadModal = () => {
    setUploadModal(false);
    setSelectedFile(null);
    setNoteType('');
    setTitle('');
    setUploading(false);
    setUploadProgress(0);
  };

  const toggleSectionExpansion = useCallback((index) => {
    setExpandedSections((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  }, []);

  // Note: Note type assignments are now initialized in handleExtractionSuccess, 
  // handlePollingSuccess, and viewGuideline based on structure detection

  // Toggle dropdown open/close
  const toggleDropdown = useCallback((key) => {
    setNoteTypeDropdowns((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  // Toggle note type for a category or subcategory
  const toggleNoteType = useCallback((categoryIndex, noteType) => {
    setNoteTypeAssignments((prev) => {
      const isCategory = typeof categoryIndex === 'number';
      // Create default note types based on group note types
      const defaultNoteTypes = createDefaultNoteTypesOff(groupNoteTypes);
      const categoryAssignments = prev[categoryIndex] || defaultNoteTypes;
      const newValue = !categoryAssignments[noteType];
      
      const updated = {
        ...prev,
        [categoryIndex]: {
          ...categoryAssignments,
          [noteType]: newValue,
        },
      };

      // If category note type is toggled, sync all requirements in that category
      if (isCategory) {
        const sections = Object.keys(localCriteria);
        const section = sections[categoryIndex];
        const criteria = localCriteria[section];
        
        // Handle both old structure (array) and new structure (object with requirements)
        const requirements = getRequirementsFromCriteria(criteria);
        
        requirements.forEach((_, requirementIndex) => {
          const requirementKey = `${categoryIndex}-${requirementIndex}`;
          const requirementAssignments = prev[requirementKey] || defaultNoteTypes;
          updated[requirementKey] = {
            ...requirementAssignments,
            [noteType]: newValue, // Sync with category (on or off)
          };
        });
      }

      return updated;
    });
  }, [localCriteria, groupNoteTypes]);

  // Note Type Dropdown Component with Multiple Selection
  const NoteTypeDropdown = ({ dropdownKey, selectedNoteTypes, categoryNoteTypes, onToggle, disabled = false }) => {
    const isOpen = noteTypeDropdowns[dropdownKey] || false;
    const selectedNoteTypesList = Object.entries(selectedNoteTypes)
      .filter(([_, isSelected]) => isSelected)
      .map(([noteType]) => noteType);
    const selectedCount = selectedNoteTypesList.length;
    
    return (
      <Dropdown 
        isOpen={isOpen} 
        toggle={() => toggleDropdown(dropdownKey)}
        style={{ display: 'inline-block' }}
      >
        <DropdownToggle
          caret
          style={{
            backgroundColor: selectedCount > 0 ? '#ffffff' : '#ffffff',
            color: '#374151',
            border: `1px solid ${selectedCount > 0 ? '#d1d5db' : '#d1d5db'}`,
            fontSize: '0.875rem',
            fontWeight: '400',
            padding: selectedCount > 0 ? '6px 14px' : '8px 14px',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            transition: 'all 0.15s ease',
            boxShadow: 'none',
            flexWrap: 'wrap',
            minHeight: '38px',
            maxWidth: '400px',
          }}
        >
          {selectedCount > 0 ? (
            <>
              {selectedNoteTypesList.map((noteType) => (
                <Badge
                  key={noteType}
                  style={{
                    backgroundColor: '#dbeafe',
                    color: '#1e40af',
                    fontSize: '0.75rem',
                    padding: '4px 10px',
                    fontWeight: '500',
                    borderRadius: '6px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    margin: '2px',
                    border: '1px solid #bfdbfe',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggle(noteType);
                  }}
                >
                  {noteType}
                  <i 
                    className="fa fa-times" 
                    style={{ 
                      fontSize: '0.7rem',
                      marginLeft: '2px',
                      cursor: 'pointer',
                      opacity: 0.7,
                    }}
                  />
                </Badge>
              ))}
            </>
          ) : (
            <span style={{ color: '#6b7280' }}>+ Add Note Types</span>
          )}
        </DropdownToggle>
        <DropdownMenu
          style={{
            maxHeight: '300px',
            overflowY: 'auto',
            minWidth: '250px',
            padding: '8px 4px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
          }}
        >
          {groupNoteTypes.length === 0 ? (
            <DropdownItem disabled style={{ color: '#dc3545', fontSize: '0.875rem', padding: '8px 12px' }}>
              Unable to load note types
            </DropdownItem>
          ) : (
            groupNoteTypes.map((noteType) => {
              const isCategoryOff = categoryNoteTypes && !categoryNoteTypes[noteType];
              const isSelected = selectedNoteTypes[noteType];
              const isDisabled = disabled || (isCategoryOff && !isSelected);
              
              return (
                <DropdownItem
                  key={noteType}
                  toggle={false}
                  onClick={(e) => {
                    e.preventDefault();
                    if (!isDisabled) {
                      onToggle(noteType);
                    }
                  }}
                  disabled={isDisabled}
                  title={isDisabled && isCategoryOff ? 'Enable this in the category header first' : ''}
                  style={{
                    padding: '0',
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    backgroundColor: isSelected ? '#e0f2fe' : 'transparent',
                    borderRadius: '6px',
                    margin: '2px 4px',
                    opacity: isDisabled ? 0.5 : 1,
                    border: 'none',
                    transition: 'background-color 0.15s ease',
                  }}
                  onMouseEnter={(e) => {
                    if (!isDisabled) {
                      e.currentTarget.style.backgroundColor = isSelected ? '#bae6fd' : '#f3f4f6';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = isSelected ? '#e0f2fe' : 'transparent';
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '10px 12px',
                    width: '100%',
                  }}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      disabled={isDisabled}
                      style={{
                        margin: 0,
                        marginRight: '12px',
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        accentColor: '#0369a1',
                        width: '18px',
                        height: '18px',
                        flexShrink: 0,
                      }}
                    />
                    <span style={{ 
                      fontSize: '0.875rem',
                      color: isSelected ? '#0369a1' : '#374151',
                      fontWeight: isSelected ? '500' : '400',
                      lineHeight: '1.5',
                      userSelect: 'none',
                    }}>
                      {noteType}
                    </span>
                  </div>
                </DropdownItem>
              );
            })
          )}
        </DropdownMenu>
      </Dropdown>
    );
  };

  // Individual Requirement Component (Subcategory)
  const RequirementItem = ({
    requirement,
    categoryIndex,
    requirementIndex,
    isReadOnly = false,
    onDelete,
  }) => {
    // Determine if required: new structure uses required boolean, old structure uses weight >= 8
    const isRequired = requirement.required !== undefined
      ? requirement.required
      : ((requirement.weight || WEIGHT_OPTIONAL) >= WEIGHT_REQUIRED);
    const usesPatientHistory = requirement.use_patient_history === true;

    const requirementKey = `${categoryIndex}-${requirementIndex}`;
    // Create default note types based on group note types
    const defaultNoteTypes = createDefaultNoteTypesOff(groupNoteTypes);
    const requirementNoteTypes = noteTypeAssignments[requirementKey] || defaultNoteTypes;
    // Get category note types to check if parent has note type disabled
    const categoryNoteTypes = noteTypeAssignments[categoryIndex] || defaultNoteTypes;

    return (
      <div
        className="mb-2"
        style={{
          backgroundColor: '#fafbfc',
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
          padding: '12px 16px',
          marginLeft: '20px',
          borderLeft: '3px solid #cbd5e1',
          position: 'relative',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ flex: '1', minWidth: '300px' }}>
            <EditableLabel
              labelText="Requirement"
              text={requirement.requirement_text || ''}
              onChange={
                isReadOnly
                  ? undefined
                  : (newText) =>
                      handleRequirementTextChange(
                        categoryIndex,
                        requirementIndex,
                        newText,
                      )
              }
              placeholder={`Describe this ${surfaceLabelLower} requirement...`}
              className="content"
              disabled={isReadOnly}
            />
          </div>
          {/* Note Type Dropdown for Requirement - Inline */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Label style={{ ...noteTypesLabelStyle, marginBottom: 0, fontSize: '0.7rem' }}>
              NOTE TYPES
            </Label>
            <NoteTypeDropdown
              dropdownKey={`requirement-${requirementKey}`}
              selectedNoteTypes={requirementNoteTypes}
              categoryNoteTypes={categoryNoteTypes}
              onToggle={(noteType) => {
                const isCategoryOff = !categoryNoteTypes[noteType];
                const isRequirementOn = requirementNoteTypes[noteType];
                // Only allow toggle if category is on, or if turning off
                if (categoryNoteTypes[noteType] || isRequirementOn) {
                  toggleNoteType(requirementKey, noteType);
                }
              }}
            />
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginLeft: 'auto',
              flexShrink: 0,
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '6px',
                cursor: isReadOnly ? 'default' : 'pointer',
              }}
              onClick={
                isReadOnly
                  ? undefined
                  : (e) => {
                      e.stopPropagation();
                      handleRequirementRequiredChange(
                        categoryIndex,
                        requirementIndex,
                        !isRequired,
                      );
                    }
              }
            >
              <input
                type="checkbox"
                checked={isRequired}
                onChange={() => {}}
                disabled={isReadOnly}
                style={{
                  width: '18px',
                  height: '18px',
                  cursor: isReadOnly ? 'not-allowed' : 'pointer',
                  accentColor: '#0369a1',
                  margin: 0,
                }}
              />
              <span style={{
                color: isRequired ? '#0369a1' : '#6b7280',
                fontSize: '0.7rem',
                fontWeight: isRequired ? '600' : '500',
                textTransform: 'uppercase',
                letterSpacing: '0.3px',
                marginTop: '2px',
              }}>
                Required
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '6px',
                cursor: isReadOnly ? 'default' : 'pointer',
              }}
              title="Also consider this patient's prior visits when grading this requirement, not just the note being scored."
              onClick={
                isReadOnly
                  ? undefined
                  : (e) => {
                      e.stopPropagation();
                      handleRequirementUseHistoryChange(
                        categoryIndex,
                        requirementIndex,
                        !usesPatientHistory,
                      );
                    }
              }
            >
              <input
                type="checkbox"
                checked={usesPatientHistory}
                onChange={() => {}}
                disabled={isReadOnly}
                style={{
                  width: '18px',
                  height: '18px',
                  cursor: isReadOnly ? 'not-allowed' : 'pointer',
                  accentColor: '#0369a1',
                  margin: 0,
                }}
              />
              <span style={{
                color: usesPatientHistory ? '#0369a1' : '#6b7280',
                fontSize: '0.7rem',
                fontWeight: usesPatientHistory ? '600' : '500',
                textTransform: 'uppercase',
                letterSpacing: '0.3px',
                marginTop: '2px',
                whiteSpace: 'nowrap',
              }}>
                Use Patient History
              </span>
            </div>
            {!isReadOnly && onDelete && (
              <Button
                size="sm"
                onClick={() => onDelete(categoryIndex, requirementIndex)}
                style={{
                  padding: '6px 10px',
                  fontSize: '0.7rem',
                  flexShrink: 0,
                  backgroundColor: '#f3f4f6',
                  color: '#6b7280',
                  border: '1px solid #e5e7eb',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#fee2e2';
                  e.currentTarget.style.color = '#dc2626';
                  e.currentTarget.style.borderColor = '#fecaca';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#f3f4f6';
                  e.currentTarget.style.color = '#6b7280';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }}
              >
                <i className="fa fa-trash"></i>
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Criteria Section Component - Updated for new structure
  const CriteriaSection = ({
    section,
    criteria,
    index,
    isExpanded,
    onToggleExpand,
    isReadOnly = false,
  }) => {
    // Handle both old structure (array) and new structure (object with requirements)
    const requirements = getRequirementsFromCriteria(criteria);
    const requirementCount = requirements.length;
    
    // Count required: new structure uses required boolean, old structure uses weight >= WEIGHT_REQUIRED
    const mustBeMetCount = requirements.filter((req) => 
      req.required !== undefined 
        ? req.required 
        : ((req.weight || WEIGHT_OPTIONAL) >= WEIGHT_REQUIRED)
    ).length;
    const niceToHaveCount = requirementCount - mustBeMetCount;
    
    // Create default note types based on group note types
    const defaultNoteTypes = createDefaultNoteTypesOff(groupNoteTypes);
    const categoryNoteTypes = noteTypeAssignments[index] || defaultNoteTypes;

    return (
      <div
        className="mb-3"
        style={{
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          backgroundColor: '#fff',
        }}
      >
        {/* Header - Always Visible */}
        <div
          style={{
            padding: '14px 16px',
            borderBottom: isExpanded ? '1px solid #e5e7eb' : 'none',
          }}
        >
          <div className="d-flex justify-content-between align-items-center">
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px' }}>
              <i
                className={`fa fa-chevron-${isExpanded ? 'down' : 'right'} text-muted`}
                style={{ cursor: 'pointer', fontSize: '0.875rem' }}
                onClick={onToggleExpand}
              ></i>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
                <div style={{ minWidth: '200px' }}>
                  <EditableLabel
                    labelText=""
                    text={section}
                    onChange={(newName) => handleSectionNameChange(index, newName)}
                    placeholder="Enter category name..."
                    className="content"
                    disabled={isReadOnly}
                  />
                </div>
                {/* Note Type Dropdown for Category - Inline */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Label style={{ ...noteTypesLabelStyle, marginBottom: 0, fontSize: '0.7rem' }}>
                    NOTE TYPES
                  </Label>
                  <NoteTypeDropdown
                    dropdownKey={`category-${index}`}
                    selectedNoteTypes={categoryNoteTypes}
                    categoryNoteTypes={null}
                    onToggle={(noteType) => toggleNoteType(index, noteType)}
                  />
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center" style={{ flexShrink: 0, marginLeft: '16px', gap: '8px' }}>
              <Badge
                style={{
                  fontSize: '0.7rem',
                  padding: '5px 10px',
                  backgroundColor: '#f1f5f9',
                  color: '#475569',
                  fontWeight: '500',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                }}
              >
                {requirementCount === 0 
                  ? 'No Requirements'
                  : `${requirementCount} requirement${requirementCount !== 1 ? 's' : ''} • ${mustBeMetCount} required`}
              </Badge>
              {!isReadOnly && (
                <Button
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteCategory(index);
                  }}
                  style={{ 
                    padding: '5px 10px', 
                    fontSize: '0.75rem',
                    backgroundColor: '#f3f4f6',
                    color: '#6b7280',
                    border: '1px solid #e5e7eb',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#fee2e2';
                    e.currentTarget.style.color = '#dc2626';
                    e.currentTarget.style.borderColor = '#fecaca';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                    e.currentTarget.style.color = '#6b7280';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }}
                >
                  <i className="fa fa-trash"></i>
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div style={{ padding: '16px 10px', backgroundColor: '#f9fafb' }}>
            {/* Requirements List */}
            {requirements.length > 0 ? (
              <div>
                {requirements.map((requirement, requirementIndex) => (
                  <RequirementItem
                    key={`requirement-${index}-${requirementIndex}`}
                    requirement={requirement}
                    categoryIndex={index}
                    requirementIndex={requirementIndex}
                    isReadOnly={isReadOnly}
                    onDelete={handleDeleteRequirement}
                  />
                ))}
              </div>
            ) : (
              <Alert color="warning">
                No requirements found for this category.
              </Alert>
            )}
            {/* Add Requirement Button */}
            {!isReadOnly && (
              <div className="mt-3">
                <Button color="primary" size="sm" onClick={() => handleAddRequirement(index)}>
                  <i className="fa fa-plus mr-2"></i>
                  Add Requirement
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <Container className="mt-4" fluid style={{ paddingLeft: '10px', paddingRight: '10px' }}>
      <Row>
        <Col style={{ paddingLeft: 0, paddingRight: 0 }}>
          {/* Header Section - Outside Card */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '10px',
            }}
          >
            <div>
              <h2 style={{ fontSize: '1.25rem' }}>
                📋 {surfaceLabel} Guidelines
              </h2>
              <div
                style={{
                  fontSize: 'small',
                  marginTop: '-10px',
                  color: '#8898aa',
                }}
              >
                Manage your {surfaceLabelLower} criteria and guidelines for
                different note types
              </div>
            </div>
          </div>

          {/* Content Section - No Card Wrapper */}
          {loading ? (
            <div className="text-center">
              <i className="fa fa-spinner fa-spin fa-2x"></i>
              <p className="mt-2">Loading guidelines...</p>
            </div>
          ) : guidelines.length === 0 ? (
            viewingCriteria ? null : (
              <div className="text-center py-5">
                <i className="fa fa-file-text-o fa-4x text-muted mb-3"></i>
                <h3 className="text-muted" style={{ fontSize: '1.0625rem' }}>
                  No {surfaceLabel} Guidelines Yet
                </h3>
                <p className="text-muted mb-4" style={{ fontSize: '1rem' }}>
                  Start from the CMS default guideline and customize it for your
                  organization. You can upload your own documents later if needed.
                </p>
                <Button
                  color="secondary"
                  onClick={handleDuplicateDefaultGuideline}
                  size="lg"
                  disabled={duplicatingDefault}
                >
                  {duplicatingDefault ? (
                    <>
                      <i className="fa fa-spinner fa-spin mr-2"></i>
                      Creating CMS Copy...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-clone mr-2"></i>
                      Start from CMS Default
                    </>
                  )}
                </Button>
              </div>
            )
          ) : (
            <div className="table-responsive-wrapper">
              <Table striped>
                <thead>
                  <tr>
                    <th>Document Name</th>
                    <th>Status</th>
                    <th>Uploaded By</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {guidelines.map((guideline) => (
                    <>
                      <tr key={guideline.guideline_id}>
                        <td>{guideline.document_name}</td>
                        <td>
                          <Badge
                            color={getGuidelineBadgeColor(guideline.status)}
                          >
                            {getGuidelineBadgeText(guideline.status)}
                          </Badge>
                        </td>
                        <td>{guideline.uploaded_by}</td>
                        <td>
                          {guideline.status === 'active' ? (
                            <>
                              <Button
                                size="sm"
                                color="info"
                                onClick={() => viewGuideline(guideline)}
                                className="mr-2"
                              >
                                <i className="fa fa-eye mr-1"></i>
                                {expandedDocument === guideline.guideline_id
                                  ? 'Hide'
                                  : 'View & Edit'}
                              </Button>
                              <Button
                                size="sm"
                                color="danger"
                                onClick={() =>
                                  deactivateActiveGuideline(guideline)
                                }
                              >
                                <i className="fa fa-ban mr-1"></i>
                                Disable
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                size="sm"
                                color="success"
                                onClick={() => activateGuideline(guideline)}
                                className="mr-2"
                              >
                                <i className="fa fa-check mr-1"></i>
                                Use for {surfaceLabel}
                              </Button>
                              <Button
                                size="sm"
                                color="info"
                                onClick={() => viewGuideline(guideline)}
                              >
                                <i className="fa fa-eye mr-1"></i>
                                {expandedDocument === guideline.guideline_id
                                  ? 'Hide'
                                  : 'View & Edit'}
                              </Button>
                            </>
                          )}
                        </td>
                      </tr>

                      {/* Inline Criteria Expansion - Right Below Each Document */}
                      {expandedDocument === guideline.guideline_id &&
                        currentGuideline && (
                          <tr>
                            <td
                              colSpan="4"
                              style={{
                                padding: '0',
                                border: 'none',
                                backgroundColor: '#f8f9fa',
                              }}
                            >
                              <div
                                style={{
                                  margin: '10px 0',
                                  padding: '20px 10px',
                                  backgroundColor: '#fff',
                                  border: '1px solid #e9ecef',
                                  borderRadius: '8px',
                                }}
                              >
                                <div
                                  className="d-flex justify-content-between align-items-start mb-4"
                                  style={{ flexWrap: 'wrap', gap: '10px' }}
                                >
                                  <h3
                                    className="mb-0"
                                    style={{
                                      fontSize: '1rem',
                                      flex: '1',
                                      minWidth: '300px',
                                    }}
                                  >
                                    {getViewingCriteriaTitle(currentGuideline)}
                                  </h3>
                                  <div
                                    style={{
                                      display: 'flex',
                                      gap: '10px',
                                      flexShrink: 0,
                                    }}
                                  >
                                    <Button
                                      color="secondary"
                                      size="sm"
                                      onClick={handleCloseWithUnsavedCheck}
                                    >
                                      <i className="fa fa-times mr-1"></i>
                                      Close
                                    </Button>
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={approveGuideline}
                                    >
                                      <i className="fa fa-save mr-1"></i>
                                      Update Criteria
                                    </Button>
                                  </div>
                                </div>

                                <div className="mb-4">
                                  <p
                                    className="text-muted mb-0"
                                    style={{ fontSize: '1rem' }}
                                  >
                                    <strong>
                                      {Object.keys(localCriteria).length}{' '}
                                      categories found
                                    </strong>{' '}
                                    - Click on any section to expand and edit
                                  </p>
                                </div>

                                {/* Criteria Sections */}
                                {Object.keys(localCriteria).length > 0 ? (
                                  <div>
                                    {Object.entries(localCriteria).map(
                                      ([section, criteria], index) => (
                                        <CriteriaSection
                                          key={`criteria-${index}`}
                                          section={section}
                                          criteria={criteria}
                                          index={index}
                                          isExpanded={expandedSections.includes(
                                            index,
                                          )}
                                          onToggleExpand={() =>
                                            toggleSectionExpansion(index)
                                          }
                                          isReadOnly={false}
                                        />
                                      ),
                                    )}
                                    <div className="mt-3">
                                      <Button color="primary" size="sm" onClick={handleAddCategory}>
                                        <i className="fa fa-plus mr-2"></i>
                                        Add Category
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <div>
                                    <Alert color="warning">
                                      <i className="fa fa-exclamation-triangle mr-2"></i>
                                      No specific criteria were extracted. You may
                                      need to review and manually add criteria.
                                    </Alert>
                                    <div className="mt-3">
                                      <Button color="primary" size="sm" onClick={handleAddCategory}>
                                        <i className="fa fa-plus mr-2"></i>
                                        Add Category
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                    </>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Col>
      </Row>

      {/* Inline Criteria Review Section - Template Manager Style */}
      {viewingCriteria && currentGuideline && (
        <Row className="mt-4" style={{ marginLeft: 0, marginRight: 0 }}>
          <Col style={{ paddingLeft: 0, paddingRight: 0 }}>
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <div>
                  <h3 className="mb-1">
                    {currentGuideline?.status === 'active'
                      ? `👁️ Viewing ${surfaceLabel} Criteria`
                      : `✅ Review Extracted ${surfaceLabel} Criteria`}
                  </h3>
                  <p className="text-muted mb-0">
                    <strong>
                      {Object.keys(localCriteria).length} requirements found
                    </strong>{' '}
                    - Click on any section to expand and edit
                  </p>
                </div>
                <div>
                  <Button
                    color="secondary"
                    onClick={handleCloseWithUnsavedCheck}
                    className="mr-2"
                  >
                    <i className="fa fa-times mr-2"></i>
                    Close
                  </Button>
                  <Button
                    color={
                      currentGuideline?.status === 'active'
                        ? 'primary'
                        : 'success'
                    }
                    onClick={approveGuideline}
                  >
                    <i className={`fa fa-save mr-2`}></i>
                    Update Criteria
                  </Button>
                </div>
              </CardHeader>
              <CardBody style={{ padding: '16px 10px' }}>
                {Object.keys(localCriteria).length > 0 ? (
                  <div>
                    {Object.entries(localCriteria).map(
                      ([section, criteria], index) => (
                        <CriteriaSection
                          key={`criteria-${index}`}
                          section={section}
                          criteria={criteria}
                          index={index}
                          isExpanded={expandedSections.includes(index)}
                          onToggleExpand={() => toggleSectionExpansion(index)}
                        />
                      ),
                    )}
                    <div className="mt-3">
                      <Button color="primary" size="sm" onClick={handleAddCategory}>
                        <i className="fa fa-plus mr-2"></i>
                        Add Category
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <Alert color="warning">
                      No specific criteria were extracted. You may need to review
                      and manually add criteria.
                    </Alert>
                    <div className="mt-3">
                      <Button color="primary" size="sm" onClick={handleAddCategory}>
                        <i className="fa fa-plus mr-2"></i>
                        Add Category
                      </Button>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* Persistent Save Ribbon when there are unsaved changes */}
      {hasPendingChanges && currentGuideline && !isSaving && (
        <div
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1050,
            backgroundColor: '#0b1b2b',
            color: '#fff',
            padding: '10px 20px',
            boxShadow: '0 -2px 10px rgba(0,0,0,0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <i className="fa fa-exclamation-circle" aria-hidden="true"></i>
            <span style={{ fontWeight: 600 }}>Unsaved changes</span>
            <span style={{ color: '#cbd5e1' }}>
              Save before navigating away to keep edits.
            </span>
          </div>
          <div style={{ display: 'flex', gap: '10px', flexShrink: 0 }}>
            <Button color="secondary" size="sm" onClick={handleDiscardChanges}>
              Discard
            </Button>
            <Button color="success" size="sm" onClick={() => approveGuideline()}>
              <i className="fa fa-save mr-1"></i>
              Save changes
            </Button>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      <Modal
        isOpen={uploadModal}
        onClose={resetUploadModal}
        buttons={[
          {
            label: 'Cancel',
            color: 'secondary',
            onClick: resetUploadModal,
            disabled: uploading,
          },
          {
            ...(() => {
              const config = getUploadButtonConfig(
                uploading,
                getCurrentActiveDocument(),
              );
              return {
                label: config.text,
                color: config.color,
                onClick: processDocument,
                disabled: !selectedFile || uploading,
              };
            })(),
          },
        ]}
        style={{ maxWidth: '500px' }}
      >
        <h2 style={{ fontSize: '1.25rem' }}>
          📄 Upload New {surfaceLabel} Guidelines
        </h2>

        {/* Warning */}
        <Alert color="warning" className="mb-3">
          <i className="fa fa-exclamation-triangle mr-2"></i>
          <strong>Important:</strong> Only one {surfaceLabelLower} document can
          be active at a time.
          {getCurrentActiveDocument()
            ? ` You may activate the new document after review; your current active document "${getCurrentActiveDocument().document_name}" will be archived when you switch.`
            : ' After extraction, you can manually activate this document for scoring.'}
        </Alert>

        <p className="text-muted mb-3" style={{ fontSize: '1rem' }}>
          Upload your {surfaceLabelLower} documents and we'll extract the key
          criteria automatically.
        </p>

        <FormGroup>
          <Label for="title" style={{ fontSize: '1rem' }}>
            Title (Optional)
          </Label>
          <Input
            type="text"
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., Updated PT Standards 2025"
          />
        </FormGroup>

        <FormGroup>
          <Label for="file">Upload Document</Label>
          <div
            style={{
              border: '2px dashed #dee2e6',
              borderRadius: '8px',
              padding: '30px',
              textAlign: 'center',
              backgroundColor: selectedFile ? '#f8f9fa' : 'transparent',
              cursor: selectedFile ? 'default' : 'pointer',
              transition: 'all 0.3s ease',
            }}
            onDragOver={(e) => {
              e.preventDefault();
              if (!selectedFile) {
                e.currentTarget.style.backgroundColor = '#f8f9fa';
              }
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.currentTarget.style.backgroundColor = selectedFile
                ? '#f8f9fa'
                : 'transparent';
            }}
            onDrop={(e) => {
              e.preventDefault();
              if (!selectedFile) {
                const files = e.dataTransfer.files;
                if (files.length > 0) {
                  const syntheticEvent = {
                    target: {
                      files: [files[0]],
                    },
                  };
                  handleFileUpload(syntheticEvent);
                }
              }
              e.currentTarget.style.backgroundColor = selectedFile
                ? '#f8f9fa'
                : 'transparent';
            }}
          >
            {selectedFile ? (
              <div>
                <i className="fa fa-file-pdf fa-2x text-success mb-2"></i>
                <p className="mb-1">
                  <strong>{selectedFile.name}</strong>
                </p>
                <p className="text-muted small mb-2">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
                <Button
                  color="link"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                  }}
                >
                  Choose Different File
                </Button>
              </div>
            ) : (
              <div onClick={() => fileInputRef.current?.click()}>
                <i className="fa fa-cloud-upload fa-2x text-muted mb-2"></i>
                <p className="mb-1">Drop your document here</p>
                <p className="text-muted small">or click to browse</p>
              </div>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />
          <small className="text-muted">
            Supported formats: PDF, DOC, DOCX (Max 10MB)
          </small>
        </FormGroup>

        {uploading && (
          <div>
            <Label>Processing Document...</Label>
            <Progress value={uploadProgress} className="mb-2">
              {uploadProgress}%
            </Progress>
            <p className="text-muted">
              {uploadProgress < 50
                ? 'Uploading document...'
                : `Extracting ${surfaceLabelLower} criteria...`}
            </p>
          </div>
        )}
      </Modal>

      {/* Progress Modal */}
      <Modal
        isOpen={progressModalOpen}
        onClose={() => {}}
        isCloseButton={false}
        style={{ maxWidth: '400px' }}
      >
        <h2 style={{ fontSize: '1.25rem' }}>
          Processing {surfaceLabel} Guidelines
        </h2>
        <p style={{ fontSize: '1rem' }}>{progressText}</p>
        {progressPercentage > 0 && progressPercentage < 100 ? (
          <Progress value={progressPercentage} color="info" className="mb-3" />
        ) : (
          ''
        )}
      </Modal>

      {/* Delete Category Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteCategoryModal.isOpen}
        onClose={() => setDeleteCategoryModal({ isOpen: false, categoryIndex: null, categoryName: '', requirementCount: 0 })}
        onConfirm={confirmDeleteCategory}
        confirmButtonText="Delete"
      >
        Are you sure you want to delete "{deleteCategoryModal.categoryName}" and {deleteCategoryModal.requirementCount} requirement{deleteCategoryModal.requirementCount !== 1 ? 's' : ''} within it?
      </ConfirmationModal>

      {/* Delete Requirement Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteRequirementModal.isOpen}
        onClose={() => setDeleteRequirementModal({ isOpen: false, categoryIndex: null, requirementIndex: null })}
        onConfirm={confirmDeleteRequirement}
        confirmButtonText="Delete"
      >
        Are you sure you want to delete this requirement?
      </ConfirmationModal>

      {/* Unsaved Changes Confirmation Modal */}
      <ConfirmationModal
        isOpen={unsavedChangesModal.isOpen}
        onClose={unsavedChangesModal.onConfirm}
        onConfirm={unsavedChangesModal.onClose || handleDiscardChanges}
        confirmButtonText="Discard"
        cancelButtonText="Save"
      >
        You have unsaved changes. Do you want to save them before closing?
      </ConfirmationModal>
    </Container>
  );
};

export default ComplianceGuidelines;
